/**
 * DataGrid Adapter for Grades
 * Transforms Grade Field Registry into MUI DataGrid column definitions
 */

import { GridColDef } from '@mui/x-data-grid';
import { Grade } from '../../../services/api/domains/grades/types';
import { GRADE_FIELDS_REGISTRY } from '../fields';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import { getGridFilterOperatorsForType } from '../../../utils/fieldOperators';
import { createCellRenderer as createSharedCellRenderer } from '../../../shared/utils/cellRenderers';

/**
 * Create a cell renderer for a specific Grade field
 * Uses shared renderer for all standard types
 */
function createGradeCellRenderer(
  field: BaseFieldDefinition<Grade>
): GridColDef['renderCell'] {
  // Use shared cell renderer factory for all fields
  return createSharedCellRenderer<Grade>(field);
}

/**
 * Convert Grade Field Registry to DataGrid column definitions
 * @returns Array of GridColDef for MUI DataGrid
 */
export function toDataGridColumns(): GridColDef<Grade>[] {
  return GRADE_FIELDS_REGISTRY.map(field => {
    // Determine filter operators based on data type
    let filterOperators;
    if (field.isFilterable) {
      // For arrays and relations, use string operators
      if (field.dataType === 'array' || field.isRelation) {
        filterOperators = getGridFilterOperatorsForType('string');
      } else {
        filterOperators = getGridFilterOperatorsForType(
          field.dataType as 'string' | 'number' | 'date'
        );
      }
    }

    const column: GridColDef<Grade> = {
      field: field.key as string,
      headerName: field.label,
      description: field.description,
      width: field.renderConfig?.width,
      minWidth: field.renderConfig?.minWidth,
      flex: field.renderConfig?.flex,
      sortable: field.isSortable ?? false,
      filterable: field.isFilterable ?? false,
      filterOperators,
      renderCell: createGradeCellRenderer(field),
    };

    return column;
  });
}

/**
 * Get default column visibility model
 * Hides metadata fields by default
 */
export function getDefaultColumnVisibility(): Record<string, boolean> {
  return {
    user_id: false,
    created_date: false,
    modified_date: false,
  };
}
